import React, { useMemo, useState } from 'react';
import { useAppDispatch } from 'reducers/index';
import type { RouteComponentProps } from 'react-router';

// Components
import BudgetSortableList from 'components/manage/budget/BudgetSortableList';
import InputFieldV3 from '@zola/zola-ui/src/components/Form/inputV3/InputFieldV3';
import SharedBudgetDrawerFooter from 'components/manage/budget/BudgetDrawer/SharedBudgetDrawerFooter';

// Constants
import { InputFieldAddOnType } from '@zola/zola-ui/src/components/Form/InputFieldV2/InputFieldAddOn';

// Context
import { useBudgetContext } from 'components/manage/budget/context';

// Types
import type { WBudgetTaxonomyNodeView } from '@zola/svc-web-api-ts-client';

// Utils
import { toastsActions } from '@zola-helpers/client/dist/es/redux/toasts';
import useEffectOnce from 'components/hooks/useEffectOnce';
import { DrawerTitle } from 'components/common/zolaUI/Drawer';
import { handleBudgetRouteChange, useLeaveBudgetRoute } from 'components/manage/budget/routes';
import { reorderTaxonomyNodes, updateBudget } from 'components/manage/budget/util/api';
import useBudgetUnsavedChanges from 'components/manage/budget/util/useBudgetUnsavedChanges';
import useBudgetSetOnSave from 'components/manage/budget/util/useBudgetSetOnSave';
import useCurrencyInput from 'components/manage/budget/util/useCurrencyInput';

// Styles
import { MaxSpendContainer, GroupsHeader, AddGroupButton } from './BudgetSettings.styles';

type RouteParams = Record<string, never>; // no params in this route

export type BudgetSettingsProps = RouteComponentProps<RouteParams, RouteParams>;

const BudgetSettings = ({
  route,
  router: { setRouteLeaveHook },
}: BudgetSettingsProps): JSX.Element => {
  // Toast
  const dispatch = useAppDispatch();
  const showErrorToast = () =>
    dispatch(
      toastsActions.negative({
        headline: 'There was an error. Please try again.',
      })
    );

  // Budget context
  const {
    actions: {
      receiveBudget,
      receiveTaxonomyNodes,
      receiveUnsavedMaxSpend,
      receiveUnsavedGroups,
      clearUnsavedMaxSpend,
      clearUnsavedGroups,
    },
    state: {
      budget: { budgeted_cents: maxSpendCents, taxonomy_nodes: groups = [], uuid: budgetUuid = '' },
      unsavedChanges: { unsavedMaxSpend, unsavedGroups },
    },
  } = useBudgetContext();

  // Drawer routing and unsaved changes setup
  useLeaveBudgetRoute(route, setRouteLeaveHook);
  const { hasUnsavedChanges, setHasUnsavedChanges } = useBudgetUnsavedChanges(() => undefined);

  // Form data initialization
  // If there are unsaved changes on coming back to settings, use those; otherwise use context/API values
  const {
    rawCurrencyValue,
    formattedCurrencyValue,
    isEditingCurrency,
    setCurrencyValue,
    toggleIsEditingCurrency,
  } = useCurrencyInput(((unsavedMaxSpend || maxSpendCents || 0) / 100).toString());
  // TODO: remove isEditingCurrency check once useCurrencyInput hook has been updated with currencyValue
  const localMaxSpend = isEditingCurrency ? rawCurrencyValue : formattedCurrencyValue;
  const [localGroups, setLocalGroups] = useState(unsavedGroups || groups);
  const localGroupUuids = useMemo(() => localGroups.map(item => item.uuid || ''), [localGroups]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleMaxSpendChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHasUnsavedChanges(true);
    setCurrencyValue(e.target.value);
  };

  const handleReorderGroups = (reorderedGroups: WBudgetTaxonomyNodeView[]) => {
    setHasUnsavedChanges(true);
    setLocalGroups(reorderedGroups);
  };

  const handleCancel = () => {
    handleBudgetRouteChange();
  };

  const handleSave = () => {
    const budgetRequest = {
      budget_uuid: budgetUuid,
      budgeted_cents: parseFloat(rawCurrencyValue) * 100,
    };
    const taxonomyNodesRequest = {
      budget_uuid: budgetUuid,
      taxonomy_node_uuids: localGroupUuids,
    };

    setIsSubmitting(true);
    setHasUnsavedChanges(false);

    Promise.all([updateBudget(budgetRequest), reorderTaxonomyNodes(taxonomyNodesRequest)])
      .then(([updatedBudget, updatedTaxonomyNodes]) => {
        receiveBudget(updatedBudget);
        receiveTaxonomyNodes(updatedTaxonomyNodes);
        dispatch(
          toastsActions.positive({
            headline: 'Updated',
          })
        );
        handleBudgetRouteChange();
      })
      .catch(() => {
        setHasUnsavedChanges(true);
        setIsSubmitting(false);
        showErrorToast();
      });
  };

  const handleStashChangesAndChangeRoute = (path: string) => {
    if (hasUnsavedChanges) {
      // Stash changes for when user comes back to settings
      receiveUnsavedMaxSpend(parseFloat(rawCurrencyValue) * 100);
      receiveUnsavedGroups(localGroups);
    }
    handleBudgetRouteChange(path);
  };

  // Handles coming back to settings with unsaved changes
  useEffectOnce(() => {
    if (unsavedMaxSpend || unsavedGroups) {
      setHasUnsavedChanges(true);
      // Component was initialized with any unsaved changes, so clear them on mount
      clearUnsavedMaxSpend();
      clearUnsavedGroups();
    }
  });

  // Handles save for drawer unsaved changes prompt
  // Wrapping inside useEffect ensures handleSave has most up-to-date values
  useBudgetSetOnSave(handleSave, [localMaxSpend, localGroups]);

  return (
    <div data-testid="BudgetSettings">
      <DrawerTitle>Settings</DrawerTitle>
      <MaxSpendContainer>
        <InputFieldV3
          id="budgeted_cents"
          name="budgeted_cents"
          label="Max spend"
          alignment="horizontal"
          addOn={InputFieldAddOnType.CASH}
          isControlled
          onChange={handleMaxSpendChange}
          onFocus={toggleIsEditingCurrency}
          onBlur={toggleIsEditingCurrency}
          value={localMaxSpend}
        />
      </MaxSpendContainer>
      <GroupsHeader>
        <h3>Groups</h3>
        <AddGroupButton
          onClick={() => handleStashChangesAndChangeRoute('/group/new')}
          variant="secondary"
        >
          Add
        </AddGroupButton>
      </GroupsHeader>
      <BudgetSortableList
        items={localGroups}
        itemUuids={localGroupUuids}
        onClickEdit={groupUuid => handleStashChangesAndChangeRoute(`/group/${groupUuid}`)}
        onReorder={handleReorderGroups}
      />
      <SharedBudgetDrawerFooter
        handleCancel={handleCancel}
        handleSave={handleSave}
        cancelDisabled={isSubmitting}
        saveDisabled={isSubmitting}
      />
    </div>
  );
};

export default BudgetSettings;
